import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowLeft,
  faSpinner,
  faTrash,
  faXmark,
} from "@fortawesome/free-solid-svg-icons";
import { useEffect, useMemo, useState } from "react";
import IServicioIndividual from "../../interfaces/IServicioIndividual";
import IModalidadPago from "../../interfaces/IModalidadPago";
import { recaudadoraLabel } from "../../hooks/usePagoOnline.hook";

interface Props {
  show: boolean;
  items: IServicioIndividual[];
  total: number;
  onClose: () => void;
  onRemove: (talon: string) => void;
  // Paso 1: pedir modalidades de pago
  onContinue: () => void;
  loadingModalidades?: boolean;
  // Paso 2: elegir una opción de pago
  modalidades: IModalidadPago[];
  onSelectModalidad: (modalidad: IModalidadPago) => void;
  payingToken?: string | null;
  onBack?: () => void;
  apiError?: string;
}

const formatARS = (n: number) =>
  new Intl.NumberFormat("es-AR", { minimumFractionDigits: 2 }).format(n);

// "MASTERCARD - " → "MASTERCARD"
const cleanModalidad = (m: string) => (m || "").replace(/\s*-\s*$/, "").trim();

export default function CartDrawer({
  show,
  items,
  total,
  onClose,
  onRemove,
  onContinue,
  loadingModalidades,
  modalidades,
  onSelectModalidad,
  payingToken,
  onBack,
  apiError,
}: Props) {
  const [selectedModalidad, setSelectedModalidad] = useState("");
  const [selectedBanco, setSelectedBanco] = useState("");

  useEffect(() => {
    if (!show) return;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = "";
    };
  }, [show]);

  const showMethods = modalidades.length > 0;
  const isPaying = payingToken != null;

  // MODALIDAD distintas (en orden de aparición) para el combo.
  const modalidadOptions = useMemo(() => {
    const seen = new Set<string>();
    const out: string[] = [];
    modalidades.forEach((m) => {
      if (!seen.has(m.MODALIDAD)) {
        seen.add(m.MODALIDAD);
        out.push(m.MODALIDAD);
      }
    });
    return out;
  }, [modalidades]);

  // Preselección: si hay una sola modalidad, elegirla; si cambian, resetear.
  useEffect(() => {
    if (modalidadOptions.length === 1) {
      setSelectedModalidad(modalidadOptions[0]);
    } else {
      setSelectedModalidad("");
    }
  }, [modalidadOptions]);

  const filtered = useMemo(
    () => modalidades.filter((m) => m.MODALIDAD === selectedModalidad),
    [modalidades, selectedModalidad]
  );

  // Segundo nivel: BANCO/tarjeta (solo cuando la modalidad trae ese prefiltro).
  const bancoOptions = useMemo(() => {
    const seen = new Set<string>();
    const out: string[] = [];
    filtered.forEach((m) => {
      const b = m.BANCO;
      if (b && b.trim() !== "" && !seen.has(b)) {
        seen.add(b);
        out.push(b);
      }
    });
    return out;
  }, [filtered]);

  const hasBanco = bancoOptions.length > 0;

  useEffect(() => {
    setSelectedBanco(bancoOptions.length === 1 ? bancoOptions[0] : "");
  }, [bancoOptions]);

  const finalOptions = useMemo(
    () => (hasBanco ? filtered.filter((m) => m.BANCO === selectedBanco) : filtered),
    [hasBanco, filtered, selectedBanco]
  );

  const handleBack = () => {
    setSelectedModalidad("");
    setSelectedBanco("");
    onBack?.();
  };

  return (
    <>
      <div
        className={`mxd-drawer-backdrop ${show ? "mxd-drawer-backdrop--show" : ""}`}
        onClick={onClose}
      />
      <div
        className={`mxd-drawer ${show ? "mxd-drawer--show" : ""}`}
        role="dialog"
        aria-modal="true"
        aria-label="Tu pedido"
      >
        <div className="mxd-drawer-grip" />
        <div className="mxd-drawer-header">
          {showMethods && onBack ? (
            <button
              type="button"
              className="mxd-drawer-close"
              onClick={handleBack}
              aria-label="Volver"
              disabled={isPaying}
            >
              <FontAwesomeIcon icon={faArrowLeft} />
            </button>
          ) : (
            <span />
          )}
          <h5 className="mxd-drawer-title">
            {showMethods ? "Elegí cómo pagar" : "Tu pedido"}
          </h5>
          <button
            type="button"
            className="mxd-drawer-close"
            onClick={onClose}
            aria-label="Cerrar"
          >
            <FontAwesomeIcon icon={faXmark} />
          </button>
        </div>

        <div className="mxd-drawer-body">
          {showMethods ? (
            <div className="mxd-pay-methods">
              <label className="mxd-pay-label" htmlFor="mxd-modalidad">
                Medio de pago
              </label>
              <select
                id="mxd-modalidad"
                className="mxd-pay-select"
                value={selectedModalidad}
                onChange={(e) => setSelectedModalidad(e.target.value)}
                disabled={isPaying}
              >
                <option value="">Seleccioná un medio…</option>
                {modalidadOptions.map((mod) => (
                  <option key={mod} value={mod}>
                    {cleanModalidad(mod)}
                  </option>
                ))}
              </select>

              {selectedModalidad && hasBanco && (
                <>
                  <label className="mxd-pay-label" htmlFor="mxd-banco">
                    Tarjeta
                  </label>
                  <select
                    id="mxd-banco"
                    className="mxd-pay-select"
                    value={selectedBanco}
                    onChange={(e) => setSelectedBanco(e.target.value)}
                    disabled={isPaying}
                  >
                    <option value="">Seleccioná una tarjeta…</option>
                    {bancoOptions.map((b) => (
                      <option key={b} value={b}>
                        {b}
                      </option>
                    ))}
                  </select>
                </>
              )}

              {selectedModalidad && (!hasBanco || selectedBanco) && (
                <div className="mxd-pay-options">
                  {finalOptions.map((m) => {
                    const loading = payingToken === m.TOKEN;
                    const primary =
                      m.CUOTAS?.trim() ||
                      recaudadoraLabel(m.RECAUDADORA, cleanModalidad(m.MODALIDAD));
                    const secondary = m.DETALLE;
                    return (
                      <button
                        key={m.TOKEN}
                        type="button"
                        className="mxd-pay-option"
                        onClick={() => onSelectModalidad(m)}
                        disabled={isPaying}
                      >
                        {loading ? (
                          <span className="mxd-pay-option-main">
                            <FontAwesomeIcon
                              icon={faSpinner}
                              className="spinner"
                            />{" "}
                            Procesando…
                          </span>
                        ) : (
                          <>
                            <span className="mxd-pay-option-left">
                              <span className="mxd-pay-option-main">
                                {primary}
                              </span>
                              {secondary && (
                                <span className="mxd-pay-option-sub">
                                  {secondary}
                                </span>
                              )}
                            </span>
                            {m.TOTAL && (
                              <span className="mxd-pay-option-total">
                                <span className="mxd-pay-option-total-label">
                                  Total Final:
                                </span>
                                <span className="mxd-pay-option-total-value">
                                  {m.TOTAL}
                                </span>
                              </span>
                            )}
                          </>
                        )}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          ) : items.length === 0 ? (
            <p className="mxd-drawer-empty">No hay items todavía.</p>
          ) : (
            items.map((it) => (
              <div className="mxd-drawer-item" key={it.NU_TALON}>
                <div className="mxd-drawer-item-info">
                  <span className="mxd-drawer-item-tag">{it.D_TIPO}</span>
                  <span className="mxd-drawer-item-title">
                    {it.D_LISTA_SERVICIOS || it.D_TIPO}
                  </span>
                  <span className="mxd-drawer-item-due">
                    Vence el {it.F_VENCIMIENTO}
                  </span>
                </div>
                <div className="mxd-drawer-item-amount">
                  ${formatARS(Number(it.IMP_PAGAR))}
                </div>
                <button
                  type="button"
                  className="mxd-drawer-item-remove"
                  onClick={() => onRemove(it.NU_TALON)}
                  aria-label="Quitar"
                >
                  <FontAwesomeIcon icon={faTrash} />
                </button>
              </div>
            ))
          )}
        </div>

        <div className="mxd-drawer-foot">
          {apiError && <div className="mxd-drawer-error">{apiError}</div>}

          <div className="mxd-drawer-summary">
            <div className="mxd-drawer-line">
              <span>Subtotal</span>
              <span>${formatARS(total)}</span>
            </div>
            <div className="mxd-drawer-line mxd-drawer-line-total">
              <span>Total</span>
              <strong>${formatARS(total)}</strong>
            </div>
          </div>

          {!showMethods && (
            <button
              type="button"
              className="mxd-drawer-pay"
              onClick={onContinue}
              disabled={items.length === 0 || loadingModalidades}
            >
              {loadingModalidades ? (
                <>
                  <FontAwesomeIcon icon={faSpinner} className="spinner" />{" "}
                  Procesando...
                </>
              ) : (
                "Continuar al pago"
              )}
            </button>
          )}
        </div>
      </div>
    </>
  );
}
